import { useEffect, useRef } from 'react';

import noop from 'lodash/noop';

import { MAP_DEFAULTS } from '@app/consts';
import { getLatLngLiteral, getLatLngOffsetByPixels } from '@app/helpers/map-utils';

const {
    WIDTH: POPOVER_WIDTH,
    HEIGHT: POPOVER_HEIGHT,
    MARGIN,
    OFFSET_Y_DEFAULT,
    OFFSET_Y_TOP,
    UI_OFFSET,
} = MAP_DEFAULTS.POPOVER;

export const useMapPopoverPosition = (
    map: google.maps.Map | null,
    selectedMarker: google.maps.marker.AdvancedMarkerElement | null,
    setPopoverPosition: React.Dispatch<React.SetStateAction<google.maps.LatLngLiteral | null>>,
    setIsPopoverTopAnchor: React.Dispatch<React.SetStateAction<boolean>>,
    zoom: number | null,
) => {
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    const anchorRef = useRef<boolean | null>(null);

    const updatePopoverPosition = () => {
        if (!map || !selectedMarker?.position || !overlayRef.current) {
            return;
        }

        const projection = overlayRef.current.getProjection();
        if (!projection) {
            return;
        }

        const latLng = getLatLngLiteral(selectedMarker.position);
        const markerLatLng = new google.maps.LatLng(latLng.lat, latLng.lng);
        const markerPoint = projection.fromLatLngToContainerPixel(markerLatLng);

        if (!markerPoint) {
            return;
        }

        const mapDiv = map.getDiv();
        const bottomRightPoint = new google.maps.Point(mapDiv.offsetWidth, mapDiv.offsetHeight);

        const verticalMargin = POPOVER_HEIGHT + MARGIN;
        const horizontalMargin = POPOVER_WIDTH / 2 + MARGIN;

        const distLeft = markerPoint.x;
        const distTop = markerPoint.y;
        const distRight = bottomRightPoint.x - markerPoint.x;
        const distBottom = bottomRightPoint.y - markerPoint.y;

        const isCloseToTop = distTop < verticalMargin + UI_OFFSET;
        const isCloseToBottom = distBottom < verticalMargin;
        const isCloseToLeft = distLeft < horizontalMargin;
        const isCloseToRight = distRight < horizontalMargin;
        const isTopAnchor = isCloseToTop && !isCloseToBottom;

        if (anchorRef.current === null) {
            anchorRef.current = isTopAnchor;
            setIsPopoverTopAnchor(isTopAnchor);
        }

        let offsetX = 0;
        const offsetY = anchorRef.current ? OFFSET_Y_TOP : OFFSET_Y_DEFAULT;

        if (isCloseToLeft) {
            offsetX = horizontalMargin - distLeft;
        } else if (isCloseToRight) {
            offsetX = -(horizontalMargin - distRight);
        }

        const newLatLng = getLatLngOffsetByPixels(map, latLng, offsetX, offsetY);
        setPopoverPosition(newLatLng ?? latLng);
    };

    useEffect(() => {
        if (!map || !selectedMarker?.position) {
            setPopoverPosition(null);

            if (overlayRef.current) {
                overlayRef.current.setMap(null);
                overlayRef.current = null;
            }

            return;
        }

        const overlay = new google.maps.OverlayView();

        overlay.onAdd = () => {
            requestAnimationFrame(() => {
                updatePopoverPosition();
            });
        };

        // Required by OverlayView lifecycle
        overlay.draw = noop;
        overlay.onRemove = noop;

        overlay.setMap(map);
        overlayRef.current = overlay;

        return () => {
            if (overlayRef.current) {
                overlayRef.current.setMap(null);
                overlayRef.current = null;
            }
            anchorRef.current = null;
        };
    }, [map, selectedMarker]);

    useEffect(() => {
        if (zoom != null && overlayRef.current) {
            updatePopoverPosition();
        }
    }, [zoom]);
};
